import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useCampaignMutation } from "./useCampaignMutation";
import type { CampaignGift, GiftOptionNode } from "../../lib/types";

interface OptionsResponse {
	gift: CampaignGift;
	items: GiftOptionNode[];
}

export const useGiftOptions = (campaignId: string, giftId: string | undefined) =>
	useQuery({
		queryKey: ["gift-options", giftId],
		queryFn: () => api.get<OptionsResponse>(`/api/campaigns/${campaignId}/gifts/${giftId}/options`),
		enabled: Boolean(giftId),
	});

const useOptionMutation = <TVariables>(
	campaignId: string,
	giftId: string,
	run: (variables: TVariables) => Promise<unknown>,
) => useCampaignMutation(campaignId, [["gift-options", giftId]], run);

export const useCreateOption = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, (label: string | null) =>
		api.post(`/api/campaigns/${campaignId}/gifts/${giftId}/options`, { label }),
	);

export const useSetOptionCategory = (campaignId: string, giftId: string) =>
	useOptionMutation(
		campaignId,
		giftId,
		({ optionId, category }: { optionId: string; category: string | null }) =>
			api.put(`/api/campaigns/${campaignId}/options/${optionId}`, { category }),
	);

export interface OptionGuideVariables {
	optionId: string;
	guideLabel?: string | null;
	guideAssetId?: string | null;
}

export const useSetOptionGuide = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, ({ optionId, ...body }: OptionGuideVariables) =>
		api.put(`/api/campaigns/${campaignId}/options/${optionId}`, body),
	);

export const useDeleteOption = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, (optionId: string) =>
		api.remove(`/api/campaigns/${campaignId}/options/${optionId}`),
	);

export interface AddProductVariables {
	optionId: string;
	productId: string;
	variantId: string | null;
	selectVariant: boolean;
	role: "primary" | "backup";
	parentId: string | null;
}

export const useAddGiftProduct = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, ({ optionId, ...body }: AddProductVariables) =>
		api.post(`/api/campaigns/${campaignId}/options/${optionId}/products`, body),
	);

export interface UpdateProductVariables {
	entryId: string;
	productId?: string;
	variantId?: string | null;
	selectVariant?: boolean;
	quantity?: number;
	msrpMinor?: number;
	availabilityFloor?: number;
	discountKind?: string;
	discountValue?: number;
	subscriptionMode?: string;
	sellingPlanGid?: string | null;
}

export const useUpdateGiftProduct = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, ({ entryId, ...body }: UpdateProductVariables) =>
		api.put(`/api/campaigns/${campaignId}/gift-products/${entryId}`, body),
	);

export const useDeleteGiftProduct = (campaignId: string, giftId: string) =>
	useOptionMutation(campaignId, giftId, (entryId: string) =>
		api.remove(`/api/campaigns/${campaignId}/gift-products/${entryId}`),
	);
